import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  FileText,
  DollarSign,
  Calendar,
  Truck,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { productsAPI } from '../services/api';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import AddProductForm from '../components/forms/AddProductForm';

// 1. Add items array to form state
const defaultForm = {
  supplier_id: '',
  invoice_number: '',
  amount: '',
  issue_date: '',
  due_date: '',
  status: 'pending',
  items: [], // Array of { product_id, item_type, side, quantity, price }
};

const AchatsFacturesFournisseurs = () => {
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Mock data for demonstration
  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setInvoices([
        {
          id: 1,
          supplier_id: 1,
          supplier_name: 'Optique Plus',
          invoice_number: 'FAC-2024-001',
          amount: 1500.00,
          issue_date: '2024-01-15',
          due_date: '2024-02-15',
          status: 'paid'
        },
        {
          id: 2,
          supplier_id: 2,
          supplier_name: 'Lunettes Pro',
          invoice_number: 'FAC-2024-002',
          amount: 2300.50,
          issue_date: '2024-01-20',
          due_date: '2024-02-20',
          status: 'pending'
        },
        {
          id: 3,
          supplier_id: 3,
          supplier_name: 'Vision Express',
          invoice_number: 'FAC-2024-003',
          amount: 890.75,
          issue_date: '2024-01-25',
          due_date: '2024-02-25',
          status: 'overdue'
        }
      ]);
      setSuppliers([
        { id: 1, name: 'Optique Plus' },
        { id: 2, name: 'Lunettes Pro' },
        { id: 3, name: 'Vision Express' }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    productsAPI.getProducts().then(res => setProducts(res.data)).catch(() => setProducts([]));
  }, []);

  // Handle form input
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // 2. Helper to add item
  const addItem = (item) => setForm(f => ({ ...f, items: [...f.items, item] }));
  const updateItem = (idx, newItem) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? newItem : it) }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        invoice_number: form.invoice_number,
        amount: form.amount,
        issue_date: form.issue_date,
        due_date: form.due_date,
        status: form.status,
        items: form.items,
      };
      if (editingId) {
        // Update existing invoice
        setInvoices(prev => prev.map(inv => 
          inv.id === editingId ? { ...inv, ...payload } : inv
        ));
        toast.success('Facture modifiée');
      } else {
        // Add new invoice
        const newInvoice = {
          id: Date.now(),
          ...payload,
          supplier_name: suppliers.find(s => s.id === parseInt(form.supplier_id))?.name || 'Fournisseur'
        };
        setInvoices(prev => [...prev, newInvoice]);
        toast.success('Facture ajoutée');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (invoice) => {
    // On edit, load items for the supplier invoice (fetch from backend if needed)
    // For now, we'll just set the form with the existing items
    setForm({
      supplier_id: invoice.supplier_id.toString(),
      invoice_number: invoice.invoice_number,
      amount: invoice.amount.toString(),
      issue_date: invoice.issue_date,
      due_date: invoice.due_date,
      status: invoice.status,
      items: invoice.items || [], // Assuming invoice.items exists and is an array
    });
    setEditingId(invoice.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      setInvoices(prev => prev.filter(inv => inv.id !== id));
      toast.success('Facture supprimée');
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'En attente' },
      paid: { variant: 'success', label: 'Payée' },
      overdue: { variant: 'danger', label: 'En retard' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <ModernBadge variant={config.variant}>{config.label}</ModernBadge>;
  };

  const filteredInvoices = (invoices || []).filter(invoice => {
    return String(invoice.supplier_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.amount || '').includes(searchTerm) ||
           String(invoice.status || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.id.toString(),
    label: supplier.name
  }));

  const statusOptions = [
    { value: 'pending', label: 'En attente' },
    { value: 'paid', label: 'Payée' },
    { value: 'overdue', label: 'En retard' }
  ];

  // Helper: options for Select
  const frameOptions = products.filter(p => p.type === 'frames' || (p.category_name && p.category_name.toLowerCase().includes('monture')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));
  const lensOptions = products.filter(p => p.type === 'glasses' || (p.category_name && p.category_name.toLowerCase().includes('verre')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));
  const accessoryOptions = products.filter(p => p.type === 'accessories' || (p.category_name && p.category_name.toLowerCase().includes('accessoire')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));

  // Helper to get product name
  const getProductName = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.name : id;
  };
  const getProductSalePrice = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.sale_price : 0;
  };
  const getProductPurchasePrice = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.purchase_price : 0;
  };
  const getItemTotal = (item) => {
    const price = item.price || getProductSalePrice(item.product_id);
    return price * (item.quantity || 1);
  };

  // Inline item form state
  const [itemForm, setItemForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [editIdx, setEditIdx] = useState(null);
  const [itemError, setItemError] = useState('');

  const resetItemForm = () => setItemForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItemForm(f => ({ ...f, [name]: value }));
  };

  const handleAddOrUpdateItem = () => {
    if (!itemForm.product_id || !itemForm.quantity || !itemForm.price) {
      setItemError('Tous les champs sont requis');
      return;
    }
    setItemError('');
    if (editIdx !== null) {
      updateItem(editIdx, itemForm);
      setEditIdx(null);
    } else {
      // Prevent duplicate product/side/type
      if (form.items.some(i => i.product_id === itemForm.product_id && i.item_type === itemForm.item_type && i.side === itemForm.side)) {
        setItemError('Cet article existe déjà');
        return;
      }
      addItem(itemForm);
    }
    resetItemForm();
  };

  const startEditItem = (idx) => {
    setEditIdx(idx);
    setItemForm(form.items[idx]);
  };

  const cancelEditItem = () => {
    setEditIdx(null);
    resetItemForm();
  };

  // 2. Add modal state for adding/editing items
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('monture');
  const [modalEditIdx, setModalEditIdx] = useState(null);
  const [modalForm, setModalForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [modalError, setModalError] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Add product modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Handle product creation success
  const handleProductCreated = async (newProduct) => {
    try {
      // Refresh products list
      const response = await productsAPI.getProducts();
      setProducts(response.data);
      // Close the modal
      setShowAddProductModal(false);
      // Show success message
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  const filterProducts = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredProducts([]);
      return;
    }
    
    const filtered = products.filter(product => {
      const name = product?.name || '';
      const model = product?.model || '';
      const brand = product?.brand || '';
      return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             model.toLowerCase().includes(searchTerm.toLowerCase()) ||
             brand.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setFilteredProducts(filtered);
  };

  const openModal = (type, idx = null) => {
    setModalType(type);
    setModalEditIdx(idx);
    if (idx !== null) {
      setModalForm(form.items[idx]);
    } else {
      setModalForm({ product_id: '', item_type: type, side: 'none', quantity: 1, price: '' });
    }
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
    setModalEditIdx(null);
    setModalError('');
  };
  const handleModalChange = (field, value) => {
    setModalForm(f => ({ ...f, [field]: value }));
  };
  const handleModalSave = () => {
    if (!modalForm.product_id || !modalForm.quantity) {
      setModalError('Tous les champs sont requis');
      return;
    }
    setModalError('');
    const price = modalForm.price !== '' && modalForm.price !== undefined ? Number(modalForm.price) : getProductPurchasePrice(modalForm.product_id);
    const itemToSave = { ...modalForm, price };
    if (modalEditIdx !== null) {
      setForm(f => ({ ...f, items: f.items.map((it, i) => i === modalEditIdx ? itemToSave : it) }));
    } else {
      if (form.items.some(i => i.product_id === modalForm.product_id && i.item_type === modalForm.item_type && i.side === modalForm.side)) {
        setModalError('Cet article existe déjà');
        return;
      }
      setForm(f => ({ ...f, items: [...f.items, itemToSave] }));
    }
    closeModal();
  };

  const tableColumns = [
    {
      key: 'supplier_name',
      label: 'Fournisseur',
      render: (value, invoice) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Truck className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{value}</div>
          </div>
        </div>
      )
    },
    {
      key: 'invoice_number',
      label: 'Numéro de facture',
      render: (value) => (
        <div className="flex items-center">
          <FileText className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-mono text-sm font-semibold text-primary-600">{value}</span>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'issue_date',
      label: 'Date d\'émission',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'due_date',
      label: 'Date d\'échéance',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, invoice) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(invoice)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(invoice)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(invoice.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalAmount = (invoices || []).reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
  const pendingInvoices = (invoices || []).filter(i => i.status === 'pending').length;
  const paidInvoices = (invoices || []).filter(i => i.status === 'paid').length;
  const overdueInvoices = (invoices || []).filter(i => i.status === 'overdue').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Factures fournisseurs</h1>
          <p className="text-neutral-600">Gérez les factures de vos fournisseurs</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des factures..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter une facture
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Factures</p>
              <p className="text-2xl font-bold text-neutral-900">{(invoices || []).length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Montant Total</p>
              <p className="text-2xl font-bold text-success-600">
                {formatCurrency(totalAmount)}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">En Attente</p>
              <p className="text-2xl font-bold text-warning-600">{pendingInvoices}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Payées</p>
              <p className="text-2xl font-bold text-info-600">{paidInvoices}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Invoices Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des factures</h3>
            <p className="text-sm text-neutral-600">{filteredInvoices.length} factures trouvées</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredInvoices}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier la facture' : 'Ajouter une facture'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Replace ModernSelect for Supplier with EntitySelectWithAdd */}
            <EntitySelectWithAdd
              entityType="supplier"
              label="Fournisseur"
              options={supplierOptions}
              value={form.supplier_id}
              onChange={value => setForm(f => ({ ...f, supplier_id: value }))}
              required
              showAddButton={true}
            />

            <ModernInput
              label="Numéro de facture"
              name="invoice_number"
              value={form.invoice_number}
              onChange={handleChange}
              placeholder="Ex: FAC-2024-001"
              icon={<FileText className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Montant"
              name="amount"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={handleChange}
              placeholder="0.00"
              icon={<DollarSign className="w-5 h-5" />}
              required
            />

            <ModernSelect
              label="Statut"
              name="status"
              value={statusOptions.find(opt => opt.value === form.status) || null}
              onChange={option => setForm(f => ({ ...f, status: option.value }))}
              options={statusOptions}
            />

            <ModernInput
              label="Date d'émission"
              name="issue_date"
              type="date"
              value={form.issue_date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Date d'échéance"
              name="due_date"
              type="date"
              value={form.due_date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
              required
            />
          </div>
          {/* Product selection section */}
          <div className="space-y-4 border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Produits</h3>
              <ModernButton
                type="button"
                variant="secondary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => openModal('monture')}
              >
                Ajouter un produit
              </ModernButton>
            </div>
            
            {/* Display added products */}
            {form.items.length > 0 ? (
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{getProductName(item.product_id)}</div>
                      <div className="text-sm text-gray-600">
                        Quantité: {item.quantity} | Prix: {formatCurrency(item.price)} | Total: {formatCurrency(getItemTotal(item))}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ModernButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        icon={<Edit className="w-4 h-4" />}
                        onClick={() => openModal(item.item_type, idx)}
                      >
                        Modifier
                      </ModernButton>
                      <ModernButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => removeItem(idx)}
                      >
                        Supprimer
                      </ModernButton>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Aucun produit ajouté
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* Item Modal */}
      <ModernModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalEditIdx !== null ? 'Modifier le produit' : 'Ajouter un produit'}
        size="md"
      >
        <div className="space-y-4">
          {/* Product Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher un produit
            </label>
            <div className="relative">
              <ModernInput
                placeholder="Tapez pour rechercher..."
                value={productSearchTerm}
                onChange={(e) => {
                  setProductSearchTerm(e.target.value);
                  filterProducts(e.target.value);
                }}
                icon={<Search className="w-5 h-5" />}
              />
              <button
                type="button"
                onClick={() => setShowAddProductModal(true)}
                className="absolute right-2 top-2 h-8 w-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                title="Ajouter un nouveau produit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Product Selection */}
          {productSearchTerm && (
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {filteredProducts.map(product => (
                <div
                  key={product.id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                    modalForm.product_id === product.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                  onClick={() => {
                    handleModalChange('product_id', product.id);
                    handleModalChange('price', product.purchase_price || 0);
                    setProductSearchTerm(product.name);
                  }}
                >
                  <div className="font-medium">{product.name || 'Produit sans nom'}</div>
                  <div className="text-sm text-gray-600">
                    Réf: {product.model || 'N/A'} | Prix: {formatCurrency(product.purchase_price || 0)}
                  </div>
        </div>
              ))}
            </div>
          )}

          {/* Quantity and Price */}
          <div className="grid grid-cols-2 gap-4">
            <ModernInput
              label="Quantité"
              type="number"
              min="1"
              value={modalForm.quantity}
              onChange={(e) => handleModalChange('quantity', parseInt(e.target.value) || 1)}
              required
            />
            <ModernInput
              label="Prix unitaire"
              type="number"
              min="0"
              step="0.01"
              value={modalForm.price}
              onChange={(e) => handleModalChange('price', parseFloat(e.target.value) || 0)}
              required
            />
        </div>

          {modalError && (
            <div className="text-red-600 text-sm">{modalError}</div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={closeModal}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="button"
              variant="primary"
              onClick={handleModalSave}
            >
              {modalEditIdx !== null ? 'Modifier' : 'Ajouter'}
          </ModernButton>
        </div>
        </div>
      </ModernModal>

      {/* Add Product Modal */}
      <ModernModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Ajouter un nouveau produit"
        size="lg"
      >
        <AddProductForm
          onSuccess={handleProductCreated}
          onCancel={() => setShowAddProductModal(false)}
        />
      </ModernModal>
    </div>
  );
};

export default AchatsFacturesFournisseurs; 